import {Loader2} from "lucide-react";
import {useEffect, useState} from "react";

import {Button} from "@/components/ui/button";
import {Checkbox} from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface StartShiftAcknowledgmentModalProps {
  readonly open: boolean;
  readonly content: string;
  readonly onAcknowledge: () => void | Promise<void>;
  readonly onRequestClose: () => void;
  readonly isPending: boolean;
}

export function StartShiftAcknowledgmentModal({
  open,
  content,
  onAcknowledge,
  onRequestClose,
  isPending,
}: Readonly<StartShiftAcknowledgmentModalProps>) {
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (open) {
      setAgreed(false);
    }
  }, [open]);

  const handleAcknowledge = async () => {
    await onAcknowledge();
  };

  const displayContent = content.trim() || "Please acknowledge to start your shift.";

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isPending) {
      onRequestClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="inset-4 max-h-[calc(100vh-2rem)] overflow-y-auto overflow-x-hidden translate-x-0 translate-y-0 sm:inset-auto sm:top-[50%] sm:left-[50%] sm:right-auto sm:bottom-auto sm:max-h-none sm:overflow-visible sm:w-full sm:max-w-3xl sm:translate-x-[-50%] sm:translate-y-[-50%]"
        showCloseButton={!isPending}
      >
        <DialogHeader>
          <DialogTitle>Start Shift Acknowledgment</DialogTitle>
          <DialogDescription>
            Please read the following and acknowledge before starting your shift.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className="min-h-80 max-h-[75vh] overflow-y-auto overflow-x-hidden rounded-md border bg-muted/50 p-4 text-sm whitespace-pre-wrap break-words"
            role="article"
          >
            {displayContent}
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="acknowledge"
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked === true)}
              disabled={isPending}
            />
            <label
              htmlFor="acknowledge"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I have read and agree to the above terms and conditions
            </label>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              className="w-full sm:w-auto sm:min-w-24"
              variant="outline"
              disabled={isPending}
              onClick={() => onRequestClose()}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="w-full sm:flex-1 sm:min-w-0"
              disabled={!agreed || isPending}
              onClick={handleAcknowledge}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" />
                  Starting...
                </>
              ) : (
                "Acknowledge"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
